import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgePercent } from 'lucide-react';
import { fmtMoney } from '../../lib/money';

const CONFETTI = ['#f59e0b', '#10b981', '#f43f5e', '#3b82f6', '#a855f7', '#f97316', '#14b8a6', '#eab308'];

/**
 * "'CELPIP50' APPLIED — CA$99.50 savings with this coupon" celebration popup.
 * data = { code, savings, total, currency, divisor }
 */
export function CouponAppliedModal({ cfg = {}, data, onClose }) {
  useEffect(() => {
    if (!data) return undefined;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [data, onClose]);
  const total = data ? fmtMoney(data.total, data.currency, data.divisor) : '';
  const title = (cfg.coupon_applied_title || '‘{code}’ applied').replace('{code}', data?.code || '');
  const bodyTpl = cfg.coupon_applied_body || 'You pay just {total} today — the discount is already on your bill.';
  const [bodyBefore, bodyAfter] = bodyTpl.includes('{total}') ? bodyTpl.split('{total}') : [bodyTpl, ''];

  return (
    <AnimatePresence>
      {data && (
        <motion.div key="coupon-applied" className="fixed inset-0 z-[140] flex items-center justify-center p-5 bg-slate-900/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="coupon-applied-modal">
          <motion.div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl text-center pt-12 pb-2 px-6 overflow-visible"
            initial={{ scale: 0.7, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }} onClick={(e) => e.stopPropagation()}>
            {/* confetti burst */}
            <div className="pointer-events-none absolute left-1/2 -top-8 w-0 h-0">
              {CONFETTI.concat(CONFETTI).map((color, i) => {
                const angle = (i / 16) * Math.PI * 2;
                const dist = 90 + (i % 3) * 30;
                return (
                  <motion.span key={i} className="absolute w-2.5 h-2.5 rounded-sm" style={{ background: color }}
                    initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
                    animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist + 40, opacity: 0, rotate: 260, scale: 0.6 }}
                    transition={{ duration: 1.1 + (i % 4) * 0.15, ease: 'easeOut' }} />
                );
              })}
            </div>
            <motion.div className="absolute -top-9 left-1/2 -translate-x-1/2 w-[76px] h-[76px] rounded-full bg-orange-500 text-white flex items-center justify-center shadow-lg ring-8 ring-white"
              initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 300, damping: 14, delay: 0.05 }}>
              <BadgePercent size={38} />
            </motion.div>
            <p className="text-sm font-extrabold tracking-widest text-slate-600 uppercase" data-testid="coupon-applied-title">{title}</p>
            <p className="text-5xl font-black text-slate-900 mt-4 tracking-tight" data-testid="coupon-applied-savings">{fmtMoney(data.savings, data.currency, data.divisor)}</p>
            <p className="text-base font-semibold text-slate-700 mt-1">{cfg.coupon_applied_savings_text || 'savings with this coupon.'}</p>
            <div className="mx-auto w-10 border-t-2 border-slate-900 my-5" />
            <p className="text-sm text-slate-500 leading-relaxed px-2">
              {bodyBefore}<span className="font-bold text-slate-900" data-testid="coupon-applied-total">{total}</span>{bodyAfter}
            </p>
            <button type="button" onClick={onClose} className="mt-6 w-full border-t border-slate-200 py-4 text-lg font-extrabold tracking-widest text-orange-500 hover:text-orange-600 transition-colors" data-testid="coupon-applied-close">{cfg.coupon_applied_close_label || 'YAY!'}</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
